import React from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Navbar from "@/components/Navbar";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import PlayerWizard from "@/pages/PlayerWizard";
import PlayerDetail from "@/pages/PlayerDetail";
import TeamBalance from "@/pages/TeamBalance";
import Tournaments from "@/pages/Tournaments";
import TournamentCreate from "@/pages/TournamentCreate";
import TournamentDetail from "@/pages/TournamentDetail";
import Training from "@/pages/Training";
import TrainingCreate from "@/pages/TrainingCreate";
import TrainingDetail from "@/pages/TrainingDetail";
import TrainingSession from "@/pages/TrainingSession";
import TrainingSessionView from "@/pages/TrainingSessionView";
import TrainingSeason from "@/pages/TrainingSeason";
import Matches from "@/pages/Matches";
import MatchCreate from "@/pages/MatchCreate";
import MatchDetail from "@/pages/MatchDetail";
import MatchSeason from "@/pages/MatchSeason";
import PlayerCompare from "@/pages/PlayerCompare";
import TeamSelect from "@/pages/TeamSelect";
import AdminHome from "@/pages/AdminHome";
import AdminTeams from "@/pages/AdminTeams";
import AdminUsers from "@/pages/AdminUsers";
import AdminLanding from "@/pages/AdminLanding";
import SuperAdmin from "@/pages/SuperAdmin";
import PerformanceTest from "@/pages/PerformanceTest";
import PerformanceTestCreate from "@/pages/PerformanceTestCreate";
import PerformanceTestDetail from "@/pages/PerformanceTestDetail";
import LandingPage from "@/pages/LandingPage";
import CookieConsentBanner from "@/components/CookieConsentBanner";

function AppLayout() {
  const { pathname } = useLocation();
  // Trainingsmodus: Read-only-Ansicht einer Trainingseinheit → Vollbild auf Mobile.
  const trainingMode = /^\/training\/[^/]+\/einheit\/ansehen$/.test(pathname) || /^\/ts\//.test(pathname);
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className={trainingMode ? "hidden md:block" : ""}>
        <Navbar />
      </div>
      <main>
        <Outlet />
      </main>
    </div>
  );
}

// Root: Landingpage für anonyme Besucher, Dashboard für eingeloggte User
function RootSwitch() {
  const { user, membershipsLoaded } = useAuth();
  if (user === null || (user && !membershipsLoaded)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-950">
        <div className="font-display text-zinc-500 tracking-wider uppercase text-sm">Lädt…</div>
      </div>
    );
  }
  if (!user) return <LandingPage />;
  // Eingeloggt → in den AppLayout-Bereich umleiten
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootSwitch />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/mannschaft-waehlen"
            element={
              <ProtectedRoute>
                <TeamSelect />
              </ProtectedRoute>
            }
          />
          <Route path="/t/:token" element={<TournamentDetail readOnly />} />
          <Route path="/ts/:token" element={<TrainingSessionView publicMode />} />
          <Route path="/ts/:slug/:token" element={<TrainingSessionView publicMode />} />
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/spieler/vergleich" element={<PlayerCompare />} />
            <Route path="/spieler/neu" element={<PlayerWizard />} />
            <Route path="/spieler/:id" element={<PlayerDetail />} />
            <Route path="/spieler/:id/bearbeiten" element={<PlayerWizard />} />
            <Route path="/team-balance" element={<TeamBalance />} />
            <Route path="/turniere" element={<Tournaments />} />
            <Route path="/turniere/neu" element={<TournamentCreate />} />
            <Route path="/turniere/:id" element={<TournamentDetail />} />
            <Route path="/training" element={<Training />} />
            <Route path="/training/saison" element={<TrainingSeason />} />
            <Route path="/training/neu" element={<TrainingCreate />} />
            <Route path="/training/:id" element={<TrainingDetail />} />
            <Route path="/training/:id/einheit/bearbeiten" element={<TrainingSession />} />
            <Route path="/training/:id/einheit/ansehen" element={<TrainingSessionView />} />
            <Route path="/leistungstest" element={<PerformanceTest />} />
            <Route path="/leistungstest/neu" element={<PerformanceTestCreate />} />
            <Route path="/leistungstest/:id" element={<PerformanceTestDetail />} />
            <Route path="/noten" element={<Matches />} />
            <Route path="/noten/saison" element={<MatchSeason />} />
            <Route path="/noten/neu" element={<MatchCreate />} />
            <Route path="/noten/:id" element={<MatchDetail />} />
            <Route path="/admin" element={<AdminHome />} />
            <Route path="/admin/teams" element={<AdminTeams />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/landing" element={<AdminLanding />} />
            <Route path="/super" element={<SuperAdmin />} />
          </Route>
        </Routes>
        <CookieConsentBanner />
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#18181b",
            color: "#f4f4f5",
            border: "1px solid #27272a",
            borderRadius: 4,
          },
        }}
      />
    </AuthProvider>
  );
}
